import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  X, Loader2, UserPlus, Sparkles, ArrowUpRight, Wand2,
} from "lucide-react";

export default function ConvertCaptureModal({ capture, onClose, onConverted }) {
  const [suggesting, setSuggesting] = useState(true);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestErr, setSuggestErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    buy_box_min_price: "",
    buy_box_max_price: "",
    buy_box_bedrooms: "",
    buy_box_property_types: "",
    markets: "",
  });

  useEffect(() => {
    let alive = true;
    setSuggesting(true);
    api.get(`/captures/${capture.id}/suggest-buy-box`)
      .then((r) => {
        if (!alive) return;
        const s = r.data || {};
        setSuggestion(s);
        setForm({
          buy_box_min_price: s.buy_box_min_price ?? "",
          buy_box_max_price: s.buy_box_max_price ?? "",
          buy_box_bedrooms: s.buy_box_bedrooms ?? "",
          buy_box_property_types: s.buy_box_property_types ?? "",
          markets: s.markets ?? "",
        });
      })
      .catch((e) => {
        if (!alive) return;
        setSuggestErr(e?.response?.data?.detail || "Could not load AI suggestion — you can still fill it in manually.");
      })
      .finally(() => { if (alive) setSuggesting(false); });
    return () => { alive = false; };
  }, [capture.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onConfirm = async () => {
    setBusy(true);
    try {
      const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const { data } = await api.post(`/captures/${capture.id}/convert`, {
        buy_box_min_price: toNum(form.buy_box_min_price),
        buy_box_max_price: toNum(form.buy_box_max_price),
        buy_box_bedrooms: toNum(form.buy_box_bedrooms),
        buy_box_property_types: form.buy_box_property_types,
        markets: form.markets,
      });
      onConverted?.(data.member);
    } finally {
      setBusy(false);
    }
  };

  const resetToSuggestion = () => {
    if (!suggestion) return;
    setForm({
      buy_box_min_price: suggestion.buy_box_min_price ?? "",
      buy_box_max_price: suggestion.buy_box_max_price ?? "",
      buy_box_bedrooms: suggestion.buy_box_bedrooms ?? "",
      buy_box_property_types: suggestion.buy_box_property_types ?? "",
      markets: suggestion.markets ?? "",
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 print:hidden"
      role="dialog"
      aria-modal="true"
      data-testid="convert-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-[var(--line)]">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "#A855F722", color: "#A855F7" }}>
              <UserPlus size={20} />
            </div>
            <div>
              <div className="label-eyebrow">Add to Cash Buyers</div>
              <h2 className="font-display font-extrabold text-xl mt-1">{capture.name}</h2>
              <div className="text-xs text-[var(--ink-3)] mt-0.5">{capture.phone}{capture.email ? ` · ${capture.email}` : ""}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" aria-label="Close" data-testid="convert-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* AI rationale */}
          <div
            className="rounded-xl p-4 border"
            style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.06) 0%, rgba(99,102,241,0.06) 100%)", borderColor: "rgba(168,85,247,0.25)" }}
            data-testid="convert-modal-rationale"
          >
            <div className="flex items-center gap-2 mb-2">
              <Wand2 size={14} style={{ color: "#A855F7" }} />
              <span className="label-eyebrow" style={{ color: "#A855F7" }}>AI-suggested buy box</span>
              {suggesting && <Loader2 size={12} className="animate-spin text-[var(--ink-3)]" />}
            </div>
            {suggesting ? (
              <p className="text-sm text-[var(--ink-2)]">Analyzing the source deal to draft a sensible buy box…</p>
            ) : suggestErr ? (
              <p className="text-sm text-[var(--warn)]">{suggestErr}</p>
            ) : (
              <p className="text-sm text-[var(--ink-2)]" data-testid="convert-modal-rationale-text">
                {suggestion?.rationale || "Suggestion ready — review and tweak below."}
              </p>
            )}
            {suggestion && !suggesting && (
              <button type="button" onClick={resetToSuggestion} className="btn-outline mt-3 !py-1.5 !px-3 text-xs" data-testid="convert-modal-reset">
                <Sparkles size={11} /> Reset to AI suggestion
              </button>
            )}
          </div>

          {/* Buy box form */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Min price" testid="convert-min-price">
              <input type="number" min="0" className="form-input" placeholder="e.g. 80000"
                     value={form.buy_box_min_price} onChange={set("buy_box_min_price")} data-testid="convert-min-price-input" />
            </Field>
            <Field label="Max price" testid="convert-max-price">
              <input type="number" min="0" className="form-input" placeholder="e.g. 280000"
                     value={form.buy_box_max_price} onChange={set("buy_box_max_price")} data-testid="convert-max-price-input" />
            </Field>
            <Field label="Min bedrooms" testid="convert-beds">
              <input type="number" min="0" className="form-input" placeholder="e.g. 3"
                     value={form.buy_box_bedrooms} onChange={set("buy_box_bedrooms")} data-testid="convert-beds-input" />
            </Field>
            <Field label="Property types" testid="convert-types">
              <input type="text" className="form-input" placeholder="SFH, Duplex"
                     value={form.buy_box_property_types} onChange={set("buy_box_property_types")} data-testid="convert-types-input" />
            </Field>
            <Field label="Markets" testid="convert-markets" full>
              <input type="text" className="form-input" placeholder="Atlanta, GA, Birmingham, AL"
                     value={form.markets} onChange={set("markets")} data-testid="convert-markets-input" />
            </Field>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-[var(--line)]">
            <button type="button" className="btn-money" onClick={onConfirm} disabled={busy} data-testid="convert-confirm-button">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              {busy ? "Adding…" : "Add Cash Buyer"}
            </button>
            <button type="button" className="btn-outline" onClick={onClose} data-testid="convert-cancel-button">
              Cancel
            </button>
            <span className="ml-auto text-xs text-[var(--ink-3)] hidden sm:inline-flex items-center gap-1">
              <ArrowUpRight size={11} /> They'll be matched against new GO deals automatically.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, full, testid, children }) {
  return (
    <label className={"block" + (full ? " sm:col-span-2" : "")} data-testid={testid}>
      <span className="label-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
